'use client'
import { Teams } from '@glovo/organisation'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import styles from './HelpCenterIframe.module.scss'
import type { FileDownloadMessage } from '@/help-center/hooks/use-bridge-communication/types'
import {
  HelpCenterMessageType,
  HostMessageType,
} from '@/help-center/hooks/use-bridge-communication/types'
import { useBridgeCommunication } from '@/help-center/hooks/use-bridge-communication/use-bridge-communication'
import { HelpCenterPanel } from '@/help-center/ui/help-center-panel/HelpCenterPanel'
import { useHelpCenter } from '@/help-center/useHelpCenter'
import { downloadFile } from '@/help-center/utils/file-management'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling, withErrorBoundary } from '@/tools/o11y/client'

const HelpCenterIframeComponent: FC = () => {
  const { t } = useLocalisation()

  const { fetchHelpCenterIframeUrl, closeHelpCenter, isHelpCenterVisible, helpCenterIframeUrl } =
    useHelpCenter()

  const { startBridgeCommunication, closeBridgeCommunication, addMessageHandler, sendMessage } =
    useBridgeCommunication()

  const { withErrorTracking, execWithErrorTracking } = useErrorHandling()

  const [isLoading, setIsLoading] = useState(false)
  const [hasLoadFailed, setHasLoadFailed] = useState(false)

  const initialiseHelpCenterIframe = useCallback(async () => {
    setIsLoading(true)
    setHasLoadFailed(false)
    try {
      const iframeUrl = await fetchHelpCenterIframeUrl()
      if (iframeUrl) {
        addMessageHandler(HelpCenterMessageType.HELPCENTER_ASKS_FOR_SHUTDOWN, () => {
          closeHelpCenter()
        })
        addMessageHandler(
          HelpCenterMessageType.HELPCENTER_ASKS_FOR_FILE_DOWNLOAD,
          async (message: FileDownloadMessage) => {
            await downloadFile(message.payload.url, 'Glovo Attachment')
            sendMessage({
              type: HostMessageType.HOST_FILE_DOWNLOAD_SUCCESS,
              correlationId: message.id,
            })
          }
        )
        startBridgeCommunication(iframeUrl)
      }
    } catch (error) {
      setHasLoadFailed(true)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [
    addMessageHandler,
    closeHelpCenter,
    fetchHelpCenterIframeUrl,
    sendMessage,
    startBridgeCommunication,
  ])

  const handleClose = closeHelpCenter

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (isHelpCenterVisible) {
        await initialiseHelpCenterIframe()
      } else {
        closeBridgeCommunication()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHelpCenterVisible])

  useEffect(
    // eslint-disable-next-line o11y-guard/error-tracking
    () =>
      withErrorTracking(() => {
        closeBridgeCommunication()
      }),
    [closeBridgeCommunication, withErrorTracking]
  )

  return (
    <HelpCenterPanel
      hasLoadFailed={hasLoadFailed}
      isLoading={isLoading}
      title={t('support_center_title')}
      onClose={handleClose}
    >
      <iframe
        className={styles.iframe}
        src={helpCenterIframeUrl ? helpCenterIframeUrl : undefined}
        title={t('support_center_title')}
      />
    </HelpCenterPanel>
  )
}

export const HelpCenterIframe = withErrorBoundary(HelpCenterIframeComponent, {
  id: 'help_center.agent_chat',
  owner: Teams.WEB,
})
